import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";
import SceneReader from "./SceneReader";
import MissingChapter from "./shared/MissingChapter";
import { useT } from "../../../stores/uiLang";
import { PE_T } from "../../../i18n/peLesson";
import type { Lang, Tier } from "../../../api/serving";

const TIERS: Tier[] = ["T1", "T2", "T3", "T4"];

interface ResolvedLesson {
  chapterId: string;
  tier: Tier;
  lang: Lang;
}

/**
 * Teacher preview of a PE lesson.
 *
 * Where StudentLessonPage resolves the lesson from the student's OWN
 * student_assignments row (and a teacher therefore has no row → MissingChapter),
 * this route resolves the assignment directly by id for its OWNER. RLS on
 * `assignments` scopes SELECT to `auth.uid() = user_id` (+ admins), so the
 * teacher who created the assignment can read it without any student row.
 *
 * It then mounts the SHARED SceneReader with role="teacher" so the doc-hub aside
 * (Observation Checklist / Family Letter / Assessment) renders. This is a
 * read-only preview: no progress persistence, and the student delivery path
 * (StudentLessonPage → LessonSequence, role="student") is left completely
 * untouched.
 */
const TeacherLessonPreview: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const user = useUserStore((state) => state.user);
  const authResolved = useUserStore((state) => state.authResolved);
  // Run E2 J5: page chrome follows the teacher's UI language (shared store).
  const t = useT(PE_T);

  const [lesson, setLesson] = useState<ResolvedLesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!authResolved) return;
    if (!user?.id || !assignmentId) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    (async () => {
      // Owner-scoped: RLS restricts assignments SELECT to the owner (and admins),
      // so no student_assignments row is required for a teacher to preview.
      const { data, error } = await supabase
        .from("assignments")
        .select("type, assessment_id")
        .eq("id", assignmentId)
        .maybeSingle();

      if (cancelled) return;

      const resolved = parseLessonKey((data as any)?.type, (data as any)?.assessment_id);

      if (error || !resolved) {
        setNotFound(true);
        setLesson(null);
      } else {
        setLesson(resolved);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [authResolved, user?.id, assignmentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-[72px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">{t.loadingPreview}</p>
        </div>
      </div>
    );
  }

  if (notFound || !lesson) {
    return (
      <div className="min-h-screen bg-gray-50 pt-[72px]">
        <MissingChapter screen="reader" />
      </div>
    );
  }

  // Wrapped in the dashboard shell: the global Header (PROJECT EXPLORE brand +
  // user) comes from DashboardLayout (router); this provides the gray-50 page
  // background + padded container matching the dashboard. role="teacher" →
  // the doc-hub aside (incl. the Assessment tab) renders inside the reader.
  return (
    <div className="min-h-screen bg-gray-50 pt-[72px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <Link
            to="/teacher/dashboard"
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
          >
            {t.backToTeacherDashboard}
          </Link>
          <h1 className="mt-1 text-xl font-bold text-gray-900 sm:text-2xl">
            {t.lessonPreview}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{t.lessonPreviewBlurb}</p>
        </div>
        <SceneReader
          chapterId={lesson.chapterId}
          tier={lesson.tier}
          lang={lesson.lang}
          role="teacher"
          embedded
        />
      </div>
    </div>
  );
};

/** Parse the composite `assessment_id` for a `pe_chapter` assignment. */
function parseLessonKey(
  type: string | undefined,
  assessmentId: string | undefined
): ResolvedLesson | null {
  if (type !== "pe_chapter" || !assessmentId) return null;
  const parts = assessmentId.split(":");
  if (parts.length !== 4 || parts[0] !== "pe") return null;

  const [, chapterId, tierRaw, langRaw] = parts;
  const tier = tierRaw.toUpperCase() as Tier;
  const lang = langRaw.toLowerCase() as Lang;

  if (!chapterId || !TIERS.includes(tier)) return null;
  if (lang !== "en" && lang !== "es") return null;

  return { chapterId, tier, lang };
}

export default TeacherLessonPreview;
